import React from 'react';
import {
  Alert,
  Form,
  Button,
  Row,
  Col,
  InputGroup,
  Card,
  Collapse,
  Dropdown,
  Spinner,
} from 'react-bootstrap';
import {VscSparkleFilled} from 'react-icons/vsc';
import type {ResumeSchema} from './resume';
import type {ResumeDbModel} from './database-models';

declare function structuredClone<T>(value: T): T;

type Props = {
  value?: ResumeDbModel;
  onChange?: (v: ResumeDbModel) => void;
  onSubmit?: (v: ResumeDbModel) => void;
  readOnly?: boolean;
  showActions?: boolean; // Controls Save/Reset visibility
  showTopActions?: boolean;
  // Inline AI rewrite (optional)
  aiShowInline?: boolean;
  aiRewrite?: (
    path: Array<string | number>,
    currentText: string,
    mode:
      | 'quantify'
      | 'shorten'
      | 'strengthen'
      | 'tone_formal'
      | 'tone_plain'
      | 'keyword_boost',
    meta?: {roleTitle?: string; targetKeywords?: string[]},
  ) => Promise<void> | void;
  aiBusyPath?: string;
  aiError?: string;
};

type BasicsProfile = NonNullable<
  NonNullable<ResumeSchema['basics']>['profiles']
>[number];

type ResumeArrayKey =
  | 'work'
  | 'volunteer'
  | 'education'
  | 'awards'
  | 'certificates'
  | 'publications'
  | 'skills'
  | 'languages'
  | 'interests'
  | 'references'
  | 'projects';

type ResumeArrayItem<K extends ResumeArrayKey> =
  NonNullable<ResumeSchema[K]> extends Array<infer T> ? T : never;

const userIdKey = 'user_id' as const;

type ResumeValidationErrors = {
  summary: string[];
};

function safeArray<T>(v: readonly T[] | undefined): T[];
function safeArray(v: unknown): unknown[];
function safeArray(v: unknown): unknown[] {
  if (!Array.isArray(v)) {
    return [];
  }

  const next: unknown[] = [];
  for (const item of v) {
    next.push(item);
  }

  return next;
}

function reorder<T>(array: T[], from: number, to: number): T[] {
  const next = [...array];
  if (to < 0 || to >= next.length || from === to) {
    return next;
  }

  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

function deepClone<T>(v: T): T {
  return structuredClone(v);
}

function makeDbModel(
  base: ResumeDbModel | undefined,
  patch: Partial<ResumeDbModel>,
): ResumeDbModel {
  return {
    ...base,
    ...patch,
    id: base?.id ?? '',
    [userIdKey]: base?.user_id ?? '',
    name: patch.name ?? base?.name ?? '',
    resume: patch.resume ?? base?.resume ?? {},
  };
}

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function addError(errors: ResumeValidationErrors, message: string): void {
  errors.summary.push(message);
}

function validateStringField(
  errors: ResumeValidationErrors,
  label: string,
  value: unknown,
): void {
  if (value === undefined || value === null) {
    return;
  }

  if (!isString(value)) {
    addError(errors, `${label} must be a string.`);
  }
}

function validateStringArrayField(
  errors: ResumeValidationErrors,
  label: string,
  value: unknown,
): void {
  if (value === undefined || value === null) {
    return;
  }

  if (!Array.isArray(value) || value.some(item => !isString(item))) {
    addError(errors, `${label} must be an array of strings.`);
  }
}

function validateArrayField(
  errors: ResumeValidationErrors,
  label: string,
  value: unknown,
  validateItem: (item: Record<string, unknown>, idx: number) => void,
): void {
  if (value === undefined || value === null) {
    return;
  }

  if (!Array.isArray(value)) {
    addError(errors, `${label} must be an array.`);
    return;
  }

  let idx = 0;
  for (const item of value) {
    if (isRecord(item)) {
      validateItem(item, idx);
    } else {
      addError(errors, `${label} ${idx + 1} must be an object.`);
    }

    idx += 1;
  }
}

export function validateResumePayload(
  payload: ResumeDbModel,
): ResumeValidationErrors {
  const errors: ResumeValidationErrors = {summary: []};

  if (!isString(payload.name)) {
    addError(errors, 'Name must be a string.');
  }

  if (!isRecord(payload.resume)) {
    addError(errors, 'Resume must be an object.');
    return errors;
  }

  const {resume} = payload;

  if (resume.basics === undefined || resume.basics === null) {
    // Nothing to validate.
  } else if (isRecord(resume.basics)) {
    validateStringField(errors, 'Basics name', resume.basics.name);
    validateStringField(errors, 'Basics label', resume.basics.label);
    validateStringField(errors, 'Basics image', resume.basics.image);
    validateStringField(errors, 'Basics email', resume.basics.email);
    validateStringField(errors, 'Basics phone', resume.basics.phone);
    validateStringField(errors, 'Basics url', resume.basics.url);
    validateStringField(errors, 'Basics summary', resume.basics.summary);

    if (
      resume.basics.location === undefined ||
      resume.basics.location === null
    ) {
      // Nothing to validate.
    } else if (isRecord(resume.basics.location)) {
      validateStringField(
        errors,
        'Basics location address',
        resume.basics.location.address,
      );
      validateStringField(
        errors,
        'Basics location postal code',
        resume.basics.location.postalCode,
      );
      validateStringField(
        errors,
        'Basics location city',
        resume.basics.location.city,
      );
      validateStringField(
        errors,
        'Basics location country code',
        resume.basics.location.countryCode,
      );
      validateStringField(
        errors,
        'Basics location region',
        resume.basics.location.region,
      );
    } else {
      addError(errors, 'Basics location must be an object.');
    }

    if (resume.basics.profiles === undefined) {
      // Nothing to validate.
    } else {
      validateArrayField(
        errors,
        'Basics profiles',
        resume.basics.profiles,
        item => {
          validateStringField(errors, 'Basics profile network', item.network);
          validateStringField(errors, 'Basics profile username', item.username);
          validateStringField(errors, 'Basics profile url', item.url);
        },
      );
    }
  } else {
    addError(errors, 'Basics must be an object.');
  }

  validateArrayField(errors, 'Work entries', resume.work, item => {
    validateStringField(errors, 'Work name', item.name);
    validateStringField(errors, 'Work location', item.location);
    validateStringField(errors, 'Work description', item.description);
    validateStringField(errors, 'Work position', item.position);
    validateStringField(errors, 'Work url', item.url);
    validateStringField(errors, 'Work start date', item.startDate);
    validateStringField(errors, 'Work end date', item.endDate);
    validateStringField(errors, 'Work summary', item.summary);
    validateStringArrayField(errors, 'Work highlights', item.highlights);
  });

  validateArrayField(errors, 'Volunteer entries', resume.volunteer, item => {
    validateStringField(errors, 'Volunteer organization', item.organization);
    validateStringField(errors, 'Volunteer position', item.position);
    validateStringField(errors, 'Volunteer url', item.url);
    validateStringField(errors, 'Volunteer start date', item.startDate);
    validateStringField(errors, 'Volunteer end date', item.endDate);
    validateStringField(errors, 'Volunteer summary', item.summary);
    validateStringArrayField(errors, 'Volunteer highlights', item.highlights);
  });

  validateArrayField(errors, 'Education entries', resume.education, item => {
    validateStringField(errors, 'Education institution', item.institution);
    validateStringField(errors, 'Education url', item.url);
    validateStringField(errors, 'Education area', item.area);
    validateStringField(errors, 'Education study type', item.studyType);
    validateStringField(errors, 'Education start date', item.startDate);
    validateStringField(errors, 'Education end date', item.endDate);
    validateStringField(errors, 'Education score', item.score);
    validateStringArrayField(errors, 'Education courses', item.courses);
  });

  validateArrayField(errors, 'Awards', resume.awards, item => {
    validateStringField(errors, 'Award title', item.title);
    validateStringField(errors, 'Award date', item.date);
    validateStringField(errors, 'Award awarder', item.awarder);
    validateStringField(errors, 'Award summary', item.summary);
  });

  validateArrayField(errors, 'Certificates', resume.certificates, item => {
    validateStringField(errors, 'Certificate name', item.name);
    validateStringField(errors, 'Certificate date', item.date);
    validateStringField(errors, 'Certificate url', item.url);
    validateStringField(errors, 'Certificate issuer', item.issuer);
  });

  validateArrayField(errors, 'Publications', resume.publications, item => {
    validateStringField(errors, 'Publication name', item.name);
    validateStringField(errors, 'Publication publisher', item.publisher);
    validateStringField(errors, 'Publication release date', item.releaseDate);
    validateStringField(errors, 'Publication url', item.url);
    validateStringField(errors, 'Publication summary', item.summary);
  });

  validateArrayField(errors, 'Skills', resume.skills, item => {
    validateStringField(errors, 'Skill name', item.name);
    validateStringField(errors, 'Skill level', item.level);
    validateStringArrayField(errors, 'Skill keywords', item.keywords);
  });

  validateArrayField(errors, 'Languages', resume.languages, item => {
    validateStringField(errors, 'Language', item.language);
    validateStringField(errors, 'Language fluency', item.fluency);
  });

  validateArrayField(errors, 'Interests', resume.interests, item => {
    validateStringField(errors, 'Interest name', item.name);
    validateStringArrayField(errors, 'Interest keywords', item.keywords);
  });

  validateArrayField(errors, 'References', resume.references, item => {
    validateStringField(errors, 'Reference name', item.name);
    validateStringField(errors, 'Reference text', item.reference);
  });

  validateArrayField(errors, 'Projects', resume.projects, item => {
    validateStringField(errors, 'Project name', item.name);
    validateStringField(errors, 'Project description', item.description);
    validateStringField(errors, 'Project summary', item.summary);
    validateStringField(errors, 'Project start date', item.startDate);
    validateStringField(errors, 'Project end date', item.endDate);
    validateStringField(errors, 'Project url', item.url);
    validateStringField(errors, 'Project entity', item.entity);
    validateStringField(errors, 'Project type', item.type);
    validateStringArrayField(errors, 'Project highlights', item.highlights);
    validateStringArrayField(errors, 'Project keywords', item.keywords);
    validateStringArrayField(errors, 'Project roles', item.roles);
  });

  if (resume.meta !== undefined && resume.meta !== null) {
    if (isRecord(resume.meta)) {
      validateStringField(errors, 'Meta canonical', resume.meta.canonical);
      validateStringField(errors, 'Meta version', resume.meta.version);
      validateStringField(
        errors,
        'Meta last modified',
        resume.meta.lastModified,
      );
    } else {
      addError(errors, 'Meta must be an object.');
    }
  }

  return errors;
}

// Helpers for inline AI
function pathKey(p: Array<string | number>): string {
  return p.map(String).join('.');
}

function AiRewriteButton({
  disabled,
  busy,
  onClick,
}: {
  disabled?: boolean;
  busy?: boolean;
  onClick: (
    mode:
      | 'quantify'
      | 'shorten'
      | 'strengthen'
      | 'tone_formal'
      | 'tone_plain'
      | 'keyword_boost',
  ) => void;
}) {
  const [open, setOpen] = React.useState(false);
  return (
    <Dropdown show={open} onToggle={setOpen} className='d-inline-block ms-2'>
      <Dropdown.Toggle
        size='sm'
        variant='outline-secondary'
        disabled={disabled}
        className='icon-btn'
      >
        {busy ? (
          <Spinner size='sm' animation='border' className='me-1' />
        ) : (
          <VscSparkleFilled
            size={16}
            className='typst-svg-override me-1 flex-shrink-0'
          />
        )}
        AI
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Item
          onClick={() => {
            onClick('quantify');
          }}
        >
          Quantify
        </Dropdown.Item>
        <Dropdown.Item
          onClick={() => {
            onClick('shorten');
          }}
        >
          Shorten
        </Dropdown.Item>
        <Dropdown.Item
          onClick={() => {
            onClick('strengthen');
          }}
        >
          Strengthen
        </Dropdown.Item>
        <Dropdown.Item
          onClick={() => {
            onClick('tone_formal');
          }}
        >
          Tone: Formal
        </Dropdown.Item>
        <Dropdown.Item
          onClick={() => {
            onClick('tone_plain');
          }}
        >
          Tone: Plain-English
        </Dropdown.Item>
        <Dropdown.Item
          onClick={() => {
            onClick('keyword_boost');
          }}
        >
          Keyword boost
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
}

/**
 * ResumeForm
 * - Fully editable ResumeSchema-backed form
 * - readOnly mode supported
 * - Handles arrays (add/remove) for repeatable sections
 * - For simple string-array fields shown as comma-separated input, caret is preserved
 */
export default function ResumeForm({
  value,
  onChange,
  onSubmit,
  readOnly,
  showActions = true,
  showTopActions = false,
  aiShowInline = false,
  aiRewrite,
  aiBusyPath,
  aiError,
}: Props): React.JSX.Element {
  const [form, setForm] = React.useState<ResumeSchema>(value?.resume ?? {});
  const [name, setName] = React.useState<string>(value?.name ?? '');
  React.useEffect(() => {
    setForm(value?.resume ?? {});
    setName(value?.name ?? '');
  }, [value]);

  // Track a baseline snapshot (initial) to enable true Reset behavior
  const baselineRef = React.useRef<ResumeDbModel | undefined>(
    value ? deepClone(value) : undefined,
  );
  React.useEffect(() => {
    if (value && value.id !== baselineRef.current?.id) {
      baselineRef.current = deepClone(value);
    }
  }, [value]);

  const [resetNonce, setResetNonce] = React.useState(0);
  const [submitAttempted, setSubmitAttempted] = React.useState(false);

  const validationErrors = React.useMemo(
    () => validateResumePayload(makeDbModel(value, {name, resume: form})),
    [form, name, value],
  );

  const emitChange = (next: ResumeSchema) => {
    setForm(next);
    onChange?.(makeDbModel(value, {name, resume: next}));
  };

  const updateBasics = (
    patch: Partial<NonNullable<ResumeSchema['basics']>>,
  ) => {
    emitChange({...form, basics: {...form.basics, ...patch}});
  };

  const updateBasicsProfile = (idx: number, patch: Partial<BasicsProfile>) => {
    const profiles = safeArray(form.basics?.profiles).map((p, i) =>
      i === idx ? {...p, ...patch} : p,
    );
    emitChange({...form, basics: {...form.basics, profiles}});
  };

  const addBasicsProfile = () => {
    updateBasics({
      profiles: [...safeArray(form.basics?.profiles), {}],
    });
  };

  const removeBasicsProfile = (idx: number) => {
    updateBasics({
      profiles: safeArray(form.basics?.profiles).filter((_, i) => i !== idx),
    });
  };

  const updateArrayItem = <K extends ResumeArrayKey>(
    key: K,
    idx: number,
    patch: Partial<ResumeArrayItem<K>>,
  ) => {
    const array = safeArray(form[key]).map((it, i) => {
      if (i !== idx) {
        return it;
      }

      const current = typeof it === 'object' && it !== null ? it : {};
      return {...current, ...patch};
    });
    emitChange({...form, [key]: array});
  };

  const addArrayItem = (key: ResumeArrayKey) => {
    const array = [...safeArray(form[key]), {}];
    emitChange({...form, [key]: array});
  };

  const removeArrayItem = (key: ResumeArrayKey, idx: number) => {
    const array = safeArray(form[key]).filter((_, i) => i !== idx);
    emitChange({...form, [key]: array});
  };

  const moveArrayItem = <K extends ResumeArrayKey>(
    key: K,
    from: number,
    dir: 'up' | 'down',
  ) => {
    const to = dir === 'up' ? from - 1 : from + 1;
    const array = reorder(safeArray(form[key]), from, to);
    emitChange({...form, [key]: array});
  };

  const moveBasicsProfile = (from: number, dir: 'up' | 'down') => {
    const to = dir === 'up' ? from - 1 : from + 1;
    const profiles = reorder(safeArray(form.basics?.profiles), from, to);
    updateBasics({profiles});
  };

  const moveWorkHighlight = (
    workIdx: number,
    from: number,
    dir: 'up' | 'down',
  ) => {
    const to = dir === 'up' ? from - 1 : from + 1;
    const w = safeArray(form.work)[workIdx];
    const nextHighlights = reorder(safeArray(w?.highlights), from, to);
    updateArrayItem('work', workIdx, {highlights: nextHighlights});
  };

  const moveVolunteerHighlight = (
    volIdx: number,
    from: number,
    dir: 'up' | 'down',
  ) => {
    const to = dir === 'up' ? from - 1 : from + 1;
    const v = safeArray(form.volunteer)[volIdx];
    const nextHighlights = reorder(safeArray(v?.highlights), from, to);
    updateArrayItem('volunteer', volIdx, {highlights: nextHighlights});
  };

  const moveProjectHighlight = (
    projIdx: number,
    from: number,
    dir: 'up' | 'down',
  ) => {
    const to = dir === 'up' ? from - 1 : from + 1;
    const p = safeArray(form.projects)[projIdx];
    const nextHighlights = reorder(safeArray(p?.highlights), from, to);
    updateArrayItem('projects', projIdx, {highlights: nextHighlights});
  };

  // Drafts for comma-separated inputs inside array items (preserve caret)
  const [drafts, setDrafts] = React.useState<Record<string, string>>({});
  React.useEffect(() => {
    // Initialize drafts from current form for known multi-value fields
    const d: Record<string, string> = {};
    const work = safeArray(form.work);
    let i = 0;
    for (const w of work) {
      d[`work-${i}-highlights`] = (w?.highlights ?? []).join(', ');
      i += 1;
    }

    const volunteer = safeArray(form.volunteer);
    i = 0;
    for (const v of volunteer) {
      d[`volunteer-${i}-highlights`] = (v?.highlights ?? []).join(', ');
      i += 1;
    }

    const education = safeArray(form.education);
    i = 0;
    for (const educationItem of education) {
      d[`education-${i}-courses`] = (educationItem?.courses ?? []).join(', ');
      i += 1;
    }

    const skills = safeArray(form.skills);
    i = 0;
    for (const s of skills) {
      d[`skills-${i}-keywords`] = (s?.keywords ?? []).join(', ');
      i += 1;
    }

    const interests = safeArray(form.interests);
    i = 0;
    for (const s of interests) {
      d[`interests-${i}-keywords`] = (s?.keywords ?? []).join(', ');
      i += 1;
    }

    const projects = safeArray(form.projects);
    i = 0;
    for (const p of projects) {
      d[`projects-${i}-keywords`] = (p?.keywords ?? []).join(', ');
      d[`projects-${i}-roles`] = (p?.roles ?? []).join(', ');
      d[`projects-${i}-highlights`] = (p?.highlights ?? []).join(', ');
      i += 1;
    }

    setDrafts(d);
  }, [form]);

  // Collapse state for each main section
  const [basicsOpen, setBasicsOpen] = React.useState<boolean>(true);
  const [workOpen, setWorkOpen] = React.useState<boolean>(true);
  const [volunteerOpen, setVolunteerOpen] = React.useState<boolean>(true);
  const [educationOpen, setEducationOpen] = React.useState<boolean>(true);
  const [awardsOpen, setAwardsOpen] = React.useState<boolean>(true);
  const [certificatesOpen, setCertificatesOpen] = React.useState<boolean>(true);
  const [publicationsOpen, setPublicationsOpen] = React.useState<boolean>(true);
  const [skillsOpen, setSkillsOpen] = React.useState<boolean>(true);
  const [languagesOpen, setLanguagesOpen] = React.useState<boolean>(true);
  const [interestsOpen, setInterestsOpen] = React.useState<boolean>(true);
  const [referencesOpen, setReferencesOpen] = React.useState<boolean>(true);
  const [projectsOpen, setProjectsOpen] = React.useState<boolean>(true);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitAttempted(true);

    if (validationErrors.summary.length > 0) {
      return;
    }

    onSubmit?.(makeDbModel(value, {name, resume: form}));
  };

  const handleReset = () => {
    // Reset strictly to baseline (initial) value; if none, clear the form
    const base = baselineRef.current;
    setSubmitAttempted(false);
    if (!base) {
      setForm({});
      setName('');
      onChange?.(makeDbModel(undefined, {name: '', resume: {}}));
      setResetNonce(n => n + 1);
      return;
    }

    const nm = base.name ?? '';
    setForm(deepClone(base.resume ?? {}));
    setName(nm);
    onChange?.(
      makeDbModel(base, {name: nm, resume: deepClone(base.resume ?? {})}),
    );
    setResetNonce(n => n + 1);
  };

  const actionButtons =
    showActions && !readOnly ? (
      <>
        <Button type='submit' className='me-2'>
          Save
        </Button>
        <Button variant='outline-secondary' onClick={handleReset}>
          Reset
        </Button>
      </>
    ) : null;

  return (
    <Form key={resetNonce} onSubmit={handleSubmit}>
      {submitAttempted && validationErrors.summary.length > 0 ? (
        <Alert variant='danger' className='mb-3'>
          <div className='fw-semibold mb-1'>
            Fix these resume format issues:
          </div>
          <ul className='mb-0 ps-3'>
            {validationErrors.summary.map((message, idx) => (
              <li key={`${message}-${idx}`}>{message}</li>
            ))}
          </ul>
        </Alert>
      ) : null}

      {showTopActions && actionButtons ? (
        <div className='d-flex justify-content-end mb-3'>{actionButtons}</div>
      ) : null}

      <Form.Group className='mb-3' controlId='resumeName'>
        <Form.Label>Name</Form.Label>
        <Form.Control
          type='text'
          value={name}
          onChange={event => {
            const newName = event.currentTarget.value;
            setName(newName);
            onChange?.(
              makeDbModel(value, {
                name: newName,
                resume: value?.resume ?? form,
              }),
            );
          }}
          readOnly={readOnly}
          placeholder='Name used to identify this resume'
        />
      </Form.Group>
      <Card className='mb-3'>
        <div className='d-flex align-items-center p-2'>
          <Button
            variant='link'
            onClick={() => {
              setBasicsOpen(!basicsOpen);
            }}
            aria-controls='basics-collapse'
            aria-expanded={basicsOpen}
            className='p-0'
          >
            <span style={{fontSize: '1rem'}}>{basicsOpen ? '▼' : '▶'}</span>
          </Button>
          <Card.Title className='ms-2 mb-0'>Basics</Card.Title>
        </div>
        <Collapse in={basicsOpen}>
          <div id='basics-collapse'>
            <Card.Body>
              <Row className='mb-3'>
                <Form.Group as={Col} controlId='basics-name'>
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    value={form.basics?.name ?? ''}
                    onChange={event => {
                      updateBasics({name: event.currentTarget.value});
                    }}
                    readOnly={readOnly}
                  />
                </Form.Group>

                <Form.Group as={Col} controlId='basics-label'>
                  <Form.Label>Label</Form.Label>
                  <div className='d-flex align-items-start'>
                    <Form.Control
                      value={form.basics?.label ?? ''}
                      onChange={event => {
                        updateBasics({label: event.currentTarget.value});
                      }}
                      readOnly={readOnly}
                    />
                    {aiShowInline && aiRewrite && (
                      <AiRewriteButton
                        disabled={readOnly ?? !(form.basics?.label ?? '')}
                        busy={
                          aiBusyPath === pathKey(['resume', 'basics', 'label'])
                        }
                        onClick={mode => {
                          void aiRewrite(
                            ['resume', 'basics', 'label'],
                            String(form.basics?.label ?? ''),
                            mode,
                          );
                        }}
                      />
                    )}
                  </div>
                  {aiError ? (
                    <div className='text-danger small mt-1'>{aiError}</div>
                  ) : null}
                </Form.Group>
              </Row>

              <Row className='mb-3'>
                <Form.Group as={Col} controlId='basics-email'>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type='email'
                    value={form.basics?.email ?? ''}
                    onChange={event => {
                      updateBasics({email: event.currentTarget.value});
                    }}
                    readOnly={readOnly}
                  />
                </Form.Group>

                <Form.Group as={Col} controlId='basics-phone'>
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    value={form.basics?.phone ?? ''}
                    onChange={event => {
                      updateBasics({phone: event.currentTarget.value});
                    }}
                    readOnly={readOnly}
                  />
                </Form.Group>
              </Row>

              <Row className='mb-3'>
                <Form.Group as={Col} controlId='basics-url'>
                  <Form.Label>Website</Form.Label>
                  <Form.Control
                    value={form.basics?.url ?? ''}
                    onChange={event => {
                      updateBasics({url: event.currentTarget.value});
                    }}
                    readOnly={readOnly}
                  />
                </Form.Group>

                <Form.Group as={Col} controlId='basics-image'>
                  <Form.Label>Image URL</Form.Label>
                  <Form.Control
                    value={form.basics?.image ?? ''}
                    onChange={event => {
                      updateBasics({image: event.currentTarget.value});
                    }}
                    readOnly={readOnly}
                  />
                </Form.Group>
              </Row>

              <Form.Group className='mb-3' controlId='basics-summary'>
                <Form.Label>Summary</Form.Label>
                <div className='d-flex align-items-start'>
                  <Form.Control
                    as='textarea'
                    rows={3}
                    value={form.basics?.summary ?? ''}
                    onChange={event => {
                      updateBasics({summary: event.currentTarget.value});
                    }}
                    readOnly={readOnly}
                  />
                  {aiShowInline && aiRewrite && (
                    <AiRewriteButton
                      disabled={readOnly ?? !(form.basics?.summary ?? '')}
                      busy={
                        aiBusyPath === pathKey(['resume', 'basics', 'summary'])
                      }
                      onClick={mode => {
                        void aiRewrite(
                          ['resume', 'basics', 'summary'],
                          String(form.basics?.summary ?? ''),
                          mode,
                        );
                      }}
                    />
                  )}
                </div>
                {aiError ? (
                  <div className='text-danger small mt-1'>{aiError}</div>
                ) : null}
              </Form.Group>

              <Card.Subtitle className='mb-2'>Location</Card.Subtitle>
              <Row className='mb-3'>
                <Form.Group as={Col} controlId='basics-location-address'>
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    value={form.basics?.location?.address ?? ''}
                    onChange={event => {
                      updateBasics({
                        location: {
                          ...form.basics?.location,
                          address: event.currentTarget.value,
                        },
                      });
                    }}
                    readOnly={readOnly}
                  />
                </Form.Group>

                <Form.Group as={Col} controlId='basics-location-city'>
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    value={form.basics?.location?.city ?? ''}
                    onChange={event => {
                      updateBasics({
                        location: {
                          ...form.basics?.location,
                          city: event.currentTarget.value,
                        },
                      });
                    }}
                    readOnly={readOnly}
                  />
                </Form.Group>
              </Row>

              <Row className='mb-3'>
                <Form.Group as={Col} controlId='basics-location-region'>
                  <Form.Label>Region</Form.Label>
                  <Form.Control
                    value={form.basics?.location?.region ?? ''}
                    onChange={event => {
                      updateBasics({
                        location: {
                          ...form.basics?.location,
                          region: event.currentTarget.value,
                        },
                      });
                    }}
                    readOnly={readOnly}
                  />
                </Form.Group>

                <Form.Group as={Col} controlId='basics-location-country'>
                  <Form.Label>Country Code</Form.Label>
                  <Form.Control
                    value={form.basics?.location?.countryCode ?? ''}
                    onChange={event => {
                      updateBasics({
                        location: {
                          ...form.basics?.location,
                          countryCode: event.currentTarget.value,
                        },
                      });
                    }}
                    readOnly={readOnly}
                  />
                </Form.Group>

                <Form.Group as={Col} controlId='basics-location-postal'>
                  <Form.Label>Postal Code</Form.Label>
                  <Form.Control
                    value={form.basics?.location?.postalCode ?? ''}
                    onChange={event => {
                      updateBasics({
                        location: {
                          ...form.basics?.location,
                          postalCode: event.currentTarget.value,
                        },
                      });
                    }}
                    readOnly={readOnly}
                  />
                </Form.Group>
              </Row>

              <Card.Subtitle className='mb-2'>Profiles</Card.Subtitle>
              {safeArray(form.basics?.profiles).map((p, idx) => (
                <Card key={`profile-${idx}`} className='mb-2 p-2'>
                  {!readOnly && (
                    <div className='d-flex justify-content-end mb-2'>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        aria-label='Move up'
                        disabled={idx === 0}
                        onClick={() => {
                          moveBasicsProfile(idx, 'up');
                        }}
                        className='me-1'
                      >
                        ↑
                      </Button>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        aria-label='Move down'
                        disabled={
                          idx === safeArray(form.basics?.profiles).length - 1
                        }
                        onClick={() => {
                          moveBasicsProfile(idx, 'down');
                        }}
                      >
                        ↓
                      </Button>
                    </div>
                  )}
                  <Row>
                    <Form.Group as={Col} controlId={`profile-network-${idx}`}>
                      <Form.Label>Network</Form.Label>
                      <Form.Control
                        value={p?.network ?? ''}
                        onChange={event => {
                          updateBasicsProfile(idx, {
                            network: event.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>

                    <Form.Group as={Col} controlId={`profile-username-${idx}`}>
                      <Form.Label>Username</Form.Label>
                      <Form.Control
                        value={p?.username ?? ''}
                        onChange={event => {
                          updateBasicsProfile(idx, {
                            username: event.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>

                    <Form.Group as={Col} controlId={`profile-url-${idx}`}>
                      <Form.Label>URL</Form.Label>
                      <Form.Control
                        value={p?.url ?? ''}
                        onChange={event => {
                          updateBasicsProfile(idx, {
                            url: event.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>
                  </Row>
                  {!readOnly && (
                    <div className='mt-2'>
                      <Button
                        variant='outline-danger'
                        onClick={() => {
                          removeBasicsProfile(idx);
                        }}
                      >
                        Remove Profile
                      </Button>
                    </div>
                  )}
                </Card>
              ))}

              {!readOnly && (
                <div>
                  <Button onClick={addBasicsProfile}>Add Profile</Button>
                </div>
              )}
            </Card.Body>
          </div>
        </Collapse>
      </Card>

      {/* Work */}
      <Card className='mb-3'>
        <div className='d-flex align-items-center p-2'>
          <Button
            variant='link'
            onClick={() => {
              setWorkOpen(!workOpen);
            }}
            aria-controls='work-collapse'
            aria-expanded={workOpen}
            className='p-0'
          >
            <span style={{fontSize: '1rem'}}>{workOpen ? '▼' : '▶'}</span>
          </Button>
          <Card.Title className='ms-2 mb-0'>Work</Card.Title>
        </div>
        <Collapse in={workOpen}>
          <div id='work-collapse'>
            <Card.Body>
              {safeArray(form.work).map((w, idx) => (
                <Card key={`work-${idx}`} className='mb-2 p-2'>
                  {!readOnly && (
                    <div className='d-flex justify-content-end mb-2'>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        aria-label='Move up'
                        disabled={idx === 0}
                        onClick={() => {
                          moveArrayItem('work', idx, 'up');
                        }}
                        className='me-1'
                      >
                        ↑
                      </Button>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        aria-label='Move down'
                        disabled={idx === safeArray(form.work).length - 1}
                        onClick={() => {
                          moveArrayItem('work', idx, 'down');
                        }}
                      >
                        ↓
                      </Button>
                    </div>
                  )}
                  <Row>
                    <Form.Group as={Col} controlId={`work-name-${idx}`}>
                      <Form.Label>Company</Form.Label>
                      <Form.Control
                        value={w?.name ?? ''}
                        onChange={event => {
                          updateArrayItem('work', idx, {
                            name: event.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>

                    <Form.Group as={Col} controlId={`work-position-${idx}`}>
                      <Form.Label>Position</Form.Label>
                      <Form.Control
                        value={w?.position ?? ''}
                        onChange={event => {
                          updateArrayItem('work', idx, {
                            position: event.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>
                  </Row>

                  <Row className='mb-2'>
                    <Form.Group as={Col} controlId={`work-location-${idx}`}>
                      <Form.Label>Location</Form.Label>
                      <Form.Control
                        value={w?.location ?? ''}
                        onChange={event => {
                          updateArrayItem('work', idx, {
                            location: event.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>

                    <Form.Group as={Col} controlId={`work-url-${idx}`}>
                      <Form.Label>URL</Form.Label>
                      <Form.Control
                        value={w?.url ?? ''}
                        onChange={event => {
                          updateArrayItem('work', idx, {
                            url: event.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>
                  </Row>

                  <Row className='mb-2'>
                    <Form.Group as={Col} controlId={`work-start-${idx}`}>
                      <Form.Label>Start Date</Form.Label>
                      <Form.Control
                        value={w?.startDate ?? ''}
                        onChange={event => {
                          updateArrayItem('work', idx, {
                            startDate: event.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>

                    <Form.Group as={Col} controlId={`work-end-${idx}`}>
                      <Form.Label>End Date</Form.Label>
                      <Form.Control
                        value={w?.endDate ?? ''}
                        onChange={event => {
                          updateArrayItem('work', idx, {
                            endDate: event.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>
                  </Row>

                  <Form.Group
                    className='mb-2'
                    controlId={`work-description-${idx}`}
                  >
                    <Form.Label>Description</Form.Label>
                    <div className='d-flex align-items-start'>
                      <Form.Control
                        as='textarea'
                        rows={2}
                        value={w?.description ?? ''}
                        onChange={event => {
                          updateArrayItem('work', idx, {
                            description: event.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                      {aiShowInline && aiRewrite && (
                        <AiRewriteButton
                          disabled={readOnly ?? !(w?.description ?? '')}
                          busy={
                            aiBusyPath ===
                            pathKey(['resume', 'work', idx, 'description'])
                          }
                          onClick={mode => {
                            void aiRewrite(
                              ['resume', 'work', idx, 'description'],
                              String(w?.description ?? ''),
                              mode,
                              {
                                roleTitle: String(w?.position ?? w?.name ?? ''),
                              },
                            );
                          }}
                        />
                      )}
                    </div>
                    {aiError ? (
                      <div className='text-danger small mt-1'>{aiError}</div>
                    ) : null}
                  </Form.Group>

                  <Form.Group
                    className='mb-2'
                    controlId={`work-summary-${idx}`}
                  >
                    <Form.Label>Summary</Form.Label>
                    <div className='d-flex align-items-start'>
                      <Form.Control
                        as='textarea'
                        rows={2}
                        value={w?.summary ?? ''}
                        onChange={event => {
                          updateArrayItem('work', idx, {
                            summary: event.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                      {aiShowInline && aiRewrite && (
                        <AiRewriteButton
                          disabled={readOnly ?? !(w?.summary ?? '')}
                          busy={
                            aiBusyPath ===
                            pathKey(['resume', 'work', idx, 'summary'])
                          }
                          onClick={mode => {
                            void aiRewrite(
                              ['resume', 'work', idx, 'summary'],
                              String(w?.summary ?? ''),
                              mode,
                              {
                                roleTitle: String(w?.position ?? w?.name ?? ''),
                              },
                            );
                          }}
                        />
                      )}
                    </div>
                    {aiError ? (
                      <div className='text-danger small mt-1'>{aiError}</div>
                    ) : null}
                  </Form.Group>

                  <Form.Group controlId={`work-highlights-${idx}`}>
                    <Form.Label>Highlights</Form.Label>
                    {safeArray(w?.highlights).length === 0 ? (
                      !readOnly && (
                        <div className='mt-2'>
                          <Button
                            onClick={() => {
                              const next = [...safeArray(w?.highlights), ''];
                              updateArrayItem('work', idx, {
                                highlights: next,
                              });
                            }}
                          >
                            Add Highlight
                          </Button>
                        </div>
                      )
                    ) : (
                      <>
                        {safeArray(w?.highlights).map((h, hIdx) => (
                          <InputGroup
                            className='mb-2'
                            key={`work-${idx}-highlight-${hIdx}`}
                          >
                            <Form.Control
                              value={typeof h === 'string' ? h : ''}
                              onChange={event => {
                                const next = safeArray(w?.highlights).map(
                                  (it, i) =>
                                    i === hIdx ? event.currentTarget.value : it,
                                );
                                updateArrayItem('work', idx, {
                                  highlights: next,
                                });
                              }}
                              readOnly={readOnly}
                              placeholder='Describe highlight'
                            />
                            {!readOnly && (
                              <>
                                <Button
                                  variant='outline-secondary'
                                  aria-label='Move up'
                                  disabled={hIdx === 0}
                                  onClick={() => {
                                    moveWorkHighlight(idx, hIdx, 'up');
                                  }}
                                  className='ms-2'
                                >
                                  ↑
                                </Button>
                                <Button
                                  variant='outline-secondary'
                                  aria-label='Move down'
                                  disabled={
                                    hIdx === safeArray(w?.highlights).length - 1
                                  }
                                  onClick={() => {
                                    moveWorkHighlight(idx, hIdx, 'down');
                                  }}
                                  className='ms-1'
                                >
                                  ↓
                                </Button>
                                <Button
                                  variant='outline-danger'
                                  onClick={() => {
                                    const next = safeArray(
                                      w?.highlights,
                                    ).filter((_, i) => i !== hIdx);
                                    updateArrayItem('work', idx, {
                                      highlights: next,
                                    });
                                  }}
                                  className='ms-1'
                                >
                                  Remove
                                </Button>
                                {aiShowInline && aiRewrite && (
                                  <AiRewriteButton
                                    disabled={readOnly ?? !h}
                                    busy={
                                      aiBusyPath ===
                                      pathKey([
                                        'resume',
                                        'work',
                                        idx,
                                        'highlights',
                                        hIdx,
                                      ])
                                    }
                                    onClick={mode => {
                                      void aiRewrite(
                                        [
                                          'resume',
                                          'work',
                                          idx,
                                          'highlights',
                                          hIdx,
                                        ],
                                        String(h ?? ''),
                                        mode,
                                        {
                                          roleTitle: String(
                                            w?.position ?? w?.name ?? '',
                                          ),
                                        },
                                      );
                                    }}
                                  />
                                )}
                              </>
                            )}
                          </InputGroup>
                        ))}
                        {!readOnly && (
                          <div>
                            <Button
                              onClick={() => {
                                const next = [...safeArray(w?.highlights), ''];
                                updateArrayItem('work', idx, {
                                  highlights: next,
                                });
                              }}
                            >
                              Add Highlight
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </Form.Group>

                  {!readOnly && (
                    <div className='mt-2'>
                      <Button
                        variant='outline-danger'
                        onClick={() => {
                          removeArrayItem('work', idx);
                        }}
                      >
                        Remove Work
                      </Button>
                    </div>
                  )}
                </Card>
              ))}

              {!readOnly && (
                <Button
                  onClick={() => {
                    addArrayItem('work');
                  }}
                >
                  Add Work
                </Button>
              )}
            </Card.Body>
          </div>
        </Collapse>
      </Card>

      {/* Volunteer */}
      <Card className='mb-3'>
        <div className='d-flex align-items-center p-2'>
          <Button
            variant='link'
            onClick={() => {
              setVolunteerOpen(!volunteerOpen);
            }}
            aria-controls='volunteer-collapse'
            aria-expanded={volunteerOpen}
            className='p-0'
          >
            <span style={{fontSize: '1rem'}}>{volunteerOpen ? '▼' : '▶'}</span>
          </Button>
          <Card.Title className='ms-2 mb-0'>Volunteer</Card.Title>
        </div>
        <Collapse in={volunteerOpen}>
          <div id='volunteer-collapse'>
            <Card.Body>
              {safeArray(form.volunteer).map((v, idx) => (
                <Card key={`vol-${idx}`} className='mb-2 p-2'>
                  {!readOnly && (
                    <div className='d-flex justify-content-end mb-2'>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        aria-label='Move up'
                        disabled={idx === 0}
                        onClick={() => {
                          moveArrayItem('volunteer', idx, 'up');
                        }}
                        className='me-1'
                      >
                        ↑
                      </Button>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        aria-label='Move down'
                        disabled={idx === safeArray(form.volunteer).length - 1}
                        onClick={() => {
                          moveArrayItem('volunteer', idx, 'down');
                        }}
                      >
                        ↓
                      </Button>
                    </div>
                  )}
                  <Row>
                    <Form.Group as={Col} controlId={`vol-name-${idx}`}>
                      <Form.Label>Organization</Form.Label>
                      <Form.Control
                        value={v?.organization ?? ''}
                        onChange={event => {
                          updateArrayItem('volunteer', idx, {
                            organization: event.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>

                    <Form.Group as={Col} controlId={`vol-position-${idx}`}>
                      <Form.Label>Position</Form.Label>
                      <Form.Control
                        value={v?.position ?? ''}
                        onChange={event => {
                          updateArrayItem('volunteer', idx, {
                            position: event.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>
                  </Row>

                  <Row className='mb-2'>
                    <Form.Group as={Col} controlId={`vol-url-${idx}`}>
                      <Form.Label>URL</Form.Label>
                      <Form.Control
                        value={v?.url ?? ''}
                        onChange={event => {
                          updateArrayItem('volunteer', idx, {
                            url: event.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>

                    <Form.Group as={Col} controlId={`vol-start-${idx}`}>
                      <Form.Label>Start Date</Form.Label>
                      <Form.Control
                        value={v?.startDate ?? ''}
                        onChange={event => {
                          updateArrayItem('volunteer', idx, {
                            startDate: event.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>

                    <Form.Group as={Col} controlId={`vol-end-${idx}`}>
                      <Form.Label>End Date</Form.Label>
                      <Form.Control
                        value={v?.endDate ?? ''}
                        onChange={event => {
                          updateArrayItem('volunteer', idx, {
                            endDate: event.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>
                  </Row>

                  <Form.Group className='mb-2' controlId={`vol-summary-${idx}`}>
                    <Form.Label>Summary</Form.Label>
                    <div className='d-flex align-items-start'>
                      <Form.Control
                        as='textarea'
                        rows={2}
                        value={v?.summary ?? ''}
                        onChange={event => {
                          updateArrayItem('volunteer', idx, {
                            summary: event.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                      {aiShowInline && aiRewrite && (
                        <AiRewriteButton
                          disabled={readOnly ?? !(v?.summary ?? '')}
                          busy={
                            aiBusyPath ===
                            pathKey(['resume', 'volunteer', idx, 'summary'])
                          }
                          onClick={mode => {
                            void aiRewrite(
                              ['resume', 'volunteer', idx, 'summary'],
                              String(v?.summary ?? ''),
                              mode,
                              {
                                roleTitle: String(
                                  v?.position ?? v?.organization ?? '',
                                ),
                              },
                            );
                          }}
                        />
                      )}
                    </div>
                    {aiError ? (
                      <div className='text-danger small mt-1'>{aiError}</div>
                    ) : null}
                  </Form.Group>

                  <Form.Group controlId={`vol-highlights-${idx}`}>
                    <Form.Label>Highlights</Form.Label>
                    {safeArray(v?.highlights).length === 0 ? (
                      !readOnly && (
                        <div className='mt-2'>
                          <Button
                            onClick={() => {
                              const next = [...safeArray(v?.highlights), ''];
                              updateArrayItem('volunteer', idx, {
                                highlights: next,
                              });
                            }}
                          >
                            Add Highlight
                          </Button>
                        </div>
                      )
                    ) : (
                      <>
                        {safeArray(v?.highlights).map((h, hIdx) => (
                          <InputGroup
                            className='mb-2'
                            key={`vol-${idx}-highlight-${hIdx}`}
                          >
                            <Form.Control
                              value={typeof h === 'string' ? h : ''}
                              onChange={event => {
                                const next = safeArray(v?.highlights).map(
                                  (it, i) =>
                                    i === hIdx ? event.currentTarget.value : it,
                                );
                                updateArrayItem('volunteer', idx, {
                                  highlights: next,
                                });
                              }}
                              readOnly={readOnly}
                              placeholder='Describe highlight'
                            />
                            {!readOnly && (
                              <>
                                <Button
                                  variant='outline-secondary'
                                  aria-label='Move up'
                                  disabled={hIdx === 0}
                                  onClick={() => {
                                    moveVolunteerHighlight(idx, hIdx, 'up');
                                  }}
                                  className='ms-2'
                                >
                                  ↑
                                </Button>
                                <Button
                                  variant='outline-secondary'
                                  aria-label='Move down'
                                  disabled={
                                    hIdx === safeArray(v?.highlights).length - 1
                                  }
                                  onClick={() => {
                                    moveVolunteerHighlight(idx, hIdx, 'down');
                                  }}
                                  className='ms-1'
                                >
                                  ↓
                                </Button>
                                <Button
                                  variant='outline-danger'
                                  onClick={() => {
                                    const next = safeArray(
                                      v?.highlights,
                                    ).filter((_, i) => i !== hIdx);
                                    updateArrayItem('volunteer', idx, {
                                      highlights: next,
                                    });
                                  }}
                                  className='ms-1'
                                >
                                  Remove
                                </Button>
                                {aiShowInline && aiRewrite && (
                                  <AiRewriteButton
                                    disabled={readOnly ?? !h}
                                    busy={
                                      aiBusyPath ===
                                      pathKey([
                                        'resume',
                                        'volunteer',
                                        idx,
                                        'highlights',
                                        hIdx,
                                      ])
                                    }
                                    onClick={mode => {
                                      void aiRewrite(
                                        [
                                          'resume',
                                          'volunteer',
                                          idx,
                                          'highlights',
                                          hIdx,
                                        ],
                                        String(h ?? ''),
                                        mode,
                                        {
                                          roleTitle: String(
                                            v?.position ??
                                              v?.organization ??
                                              '',
                                          ),
                                        },
                                      );
                                    }}
                                  />
                                )}
                              </>
                            )}
                          </InputGroup>
                        ))}
                        {!readOnly && (
                          <div>
                            <Button
                              onClick={() => {
                                const next = [...safeArray(v?.highlights), ''];
                                updateArrayItem('volunteer', idx, {
                                  highlights: next,
                                });
                              }}
                            >
                              Add Highlight
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </Form.Group>

                  {!readOnly && (
                    <div className='mt-2'>
                      <Button
                        variant='outline-danger'
                        onClick={() => {
                          removeArrayItem('volunteer', idx);
                        }}
                      >
                        Remove Volunteer
                      </Button>
                    </div>
                  )}
                </Card>
              ))}

              {!readOnly && (
                <Button
                  onClick={() => {
                    addArrayItem('volunteer');
                  }}
                >
                  Add Volunteer
                </Button>
              )}
            </Card.Body>
          </div>
        </Collapse>
      </Card>

      {/* Education */}
      <Card className='mb-3'>
        <div className='d-flex align-items-center p-2'>
          <Button
            variant='link'
            onClick={() => {
              setEducationOpen(!educationOpen);
            }}
            aria-controls='education-collapse'
            aria-expanded={educationOpen}
            className='p-0'
          >
            <span style={{fontSize: '1rem'}}>{educationOpen ? '▼' : '▶'}</span>
          </Button>
          <Card.Title className='ms-2 mb-0'>Education</Card.Title>
        </div>
        <Collapse in={educationOpen}>
          <div id='education-collapse'>
            <Card.Body>
              {safeArray(form.education).map((educationItem, idx) => (
                <Card key={`edu-${idx}`} className='mb-2 p-2'>
                  {!readOnly && (
                    <div className='d-flex justify-content-end mb-2'>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        aria-label='Move up'
                        disabled={idx === 0}
                        onClick={() => {
                          moveArrayItem('education', idx, 'up');
                        }}
                        className='me-1'
                      >
                        ↑
                      </Button>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        aria-label='Move down'
                        disabled={idx === safeArray(form.education).length - 1}
                        onClick={() => {
                          moveArrayItem('education', idx, 'down');
                        }}
                      >
                        ↓
                      </Button>
                    </div>
                  )}
                  <Row>
                    <Form.Group as={Col} controlId={`edu-inst-${idx}`}>
                      <Form.Label>Institution</Form.Label>
                      <Form.Control
                        value={educationItem?.institution ?? ''}
                        onChange={ev => {
                          updateArrayItem('education', idx, {
                            institution: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>

                    <Form.Group as={Col} controlId={`edu-area-${idx}`}>
                      <Form.Label>Area</Form.Label>
                      <Form.Control
                        value={educationItem?.area ?? ''}
                        onChange={ev => {
                          updateArrayItem('education', idx, {
                            area: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>
                  </Row>

                  <Row className='mb-2'>
                    <Form.Group as={Col} controlId={`edu-url-${idx}`}>
                      <Form.Label>URL</Form.Label>
                      <Form.Control
                        value={educationItem?.url ?? ''}
                        onChange={ev => {
                          updateArrayItem('education', idx, {
                            url: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>

                    <Form.Group as={Col} controlId={`edu-studyType-${idx}`}>
                      <Form.Label>Study Type</Form.Label>
                      <Form.Control
                        value={educationItem?.studyType ?? ''}
                        onChange={ev => {
                          updateArrayItem('education', idx, {
                            studyType: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>
                  </Row>

                  <Row className='mb-2'>
                    <Form.Group as={Col} controlId={`edu-start-${idx}`}>
                      <Form.Label>Start Date</Form.Label>
                      <Form.Control
                        value={educationItem?.startDate ?? ''}
                        onChange={ev => {
                          updateArrayItem('education', idx, {
                            startDate: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>

                    <Form.Group as={Col} controlId={`edu-end-${idx}`}>
                      <Form.Label>End Date</Form.Label>
                      <Form.Control
                        value={educationItem?.endDate ?? ''}
                        onChange={ev => {
                          updateArrayItem('education', idx, {
                            endDate: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>
                  </Row>

                  <Form.Group className='mb-2' controlId={`edu-score-${idx}`}>
                    <Form.Label>Score</Form.Label>
                    <Form.Control
                      value={educationItem?.score ?? ''}
                      onChange={ev => {
                        updateArrayItem('education', idx, {
                          score: ev.currentTarget.value,
                        });
                      }}
                      readOnly={readOnly}
                    />
                  </Form.Group>

                  <Form.Group controlId={`edu-courses-${idx}`}>
                    <Form.Label>Courses (comma separated)</Form.Label>
                    <Form.Control
                      value={
                        drafts[`education-${idx}-courses`] ??
                        educationItem?.courses?.join(', ') ??
                        ''
                      }
                      onChange={ev => {
                        const value_ = ev.currentTarget.value;
                        setDrafts(previous => ({
                          ...previous,
                          [`education-${idx}-courses`]: value_,
                        }));
                      }}
                      onBlur={ev => {
                        const value_ = ev.currentTarget.value;
                        const array = value_
                          .split(',')
                          .map(s => s.trim())
                          .filter(Boolean);
                        updateArrayItem('education', idx, {courses: array});
                        setDrafts(previous => ({
                          ...previous,
                          [`education-${idx}-courses`]: array.join(', '),
                        }));
                      }}
                      readOnly={readOnly}
                    />
                  </Form.Group>

                  {!readOnly && (
                    <div className='mt-2'>
                      <Button
                        variant='outline-danger'
                        onClick={() => {
                          removeArrayItem('education', idx);
                        }}
                      >
                        Remove Education
                      </Button>
                    </div>
                  )}
                </Card>
              ))}

              {!readOnly && (
                <Button
                  onClick={() => {
                    addArrayItem('education');
                  }}
                >
                  Add Education
                </Button>
              )}
            </Card.Body>
          </div>
        </Collapse>
      </Card>

      {/* Awards */}
      <Card className='mb-3'>
        <div className='d-flex align-items-center p-2'>
          <Button
            variant='link'
            onClick={() => {
              setAwardsOpen(!awardsOpen);
            }}
            aria-controls='awards-collapse'
            aria-expanded={awardsOpen}
            className='p-0'
          >
            <span style={{fontSize: '1rem'}}>{awardsOpen ? '▼' : '▶'}</span>
          </Button>
          <Card.Title className='ms-2 mb-0'>Awards</Card.Title>
        </div>
        <Collapse in={awardsOpen}>
          <div id='awards-collapse'>
            <Card.Body>
              {safeArray(form.awards).map((a, idx) => (
                <Card key={`aw-${idx}`} className='mb-2 p-2'>
                  {!readOnly && (
                    <div className='d-flex justify-content-end mb-2'>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        aria-label='Move up'
                        disabled={idx === 0}
                        onClick={() => {
                          moveArrayItem('awards', idx, 'up');
                        }}
                        className='me-1'
                      >
                        ↑
                      </Button>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        aria-label='Move down'
                        disabled={idx === safeArray(form.awards).length - 1}
                        onClick={() => {
                          moveArrayItem('awards', idx, 'down');
                        }}
                      >
                        ↓
                      </Button>
                    </div>
                  )}
                  <Row>
                    <Form.Group as={Col} controlId={`award-title-${idx}`}>
                      <Form.Label>Title</Form.Label>
                      <Form.Control
                        value={a?.title ?? ''}
                        onChange={ev => {
                          updateArrayItem('awards', idx, {
                            title: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>

                    <Form.Group as={Col} controlId={`award-awarder-${idx}`}>
                      <Form.Label>Awarder</Form.Label>
                      <Form.Control
                        value={a?.awarder ?? ''}
                        onChange={ev => {
                          updateArrayItem('awards', idx, {
                            awarder: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>
                  </Row>

                  <Row className='mb-2'>
                    <Form.Group as={Col} controlId={`award-date-${idx}`}>
                      <Form.Label>Date</Form.Label>
                      <Form.Control
                        value={a?.date ?? ''}
                        onChange={ev => {
                          updateArrayItem('awards', idx, {
                            date: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>
                  </Row>

                  <Form.Group controlId={`award-summary-${idx}`}>
                    <Form.Label>Summary</Form.Label>
                    <div className='d-flex align-items-start'>
                      <Form.Control
                        as='textarea'
                        rows={2}
                        value={a?.summary ?? ''}
                        onChange={ev => {
                          updateArrayItem('awards', idx, {
                            summary: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                      {aiShowInline && aiRewrite && (
                        <AiRewriteButton
                          disabled={readOnly ?? !(a?.summary ?? '')}
                          busy={
                            aiBusyPath ===
                            pathKey(['resume', 'awards', idx, 'summary'])
                          }
                          onClick={mode => {
                            void aiRewrite(
                              ['resume', 'awards', idx, 'summary'],
                              String(a?.summary ?? ''),
                              mode,
                            );
                          }}
                        />
                      )}
                    </div>
                    {aiError ? (
                      <div className='text-danger small mt-1'>{aiError}</div>
                    ) : null}
                  </Form.Group>

                  {!readOnly && (
                    <div className='mt-2'>
                      <Button
                        variant='outline-danger'
                        onClick={() => {
                          removeArrayItem('awards', idx);
                        }}
                      >
                        Remove Award
                      </Button>
                    </div>
                  )}
                </Card>
              ))}

              {!readOnly && (
                <Button
                  onClick={() => {
                    addArrayItem('awards');
                  }}
                >
                  Add Award
                </Button>
              )}
            </Card.Body>
          </div>
        </Collapse>
      </Card>

      {/* Certificates */}
      <Card className='mb-3'>
        <div className='d-flex align-items-center p-2'>
          <Button
            variant='link'
            onClick={() => {
              setCertificatesOpen(!certificatesOpen);
            }}
            aria-controls='certificates-collapse'
            aria-expanded={certificatesOpen}
            className='p-0'
          >
            <span style={{fontSize: '1rem'}}>
              {certificatesOpen ? '▼' : '▶'}
            </span>
          </Button>
          <Card.Title className='ms-2 mb-0'>Certificates</Card.Title>
        </div>
        <Collapse in={certificatesOpen}>
          <div id='certificates-collapse'>
            <Card.Body>
              {safeArray(form.certificates).map((c, idx) => (
                <Card key={`cert-${idx}`} className='mb-2 p-2'>
                  {!readOnly && (
                    <div className='d-flex justify-content-end mb-2'>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        aria-label='Move up'
                        disabled={idx === 0}
                        onClick={() => {
                          moveArrayItem('certificates', idx, 'up');
                        }}
                        className='me-1'
                      >
                        ↑
                      </Button>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        aria-label='Move down'
                        disabled={
                          idx === safeArray(form.certificates).length - 1
                        }
                        onClick={() => {
                          moveArrayItem('certificates', idx, 'down');
                        }}
                      >
                        ↓
                      </Button>
                    </div>
                  )}
                  <Row>
                    <Form.Group as={Col} controlId={`cert-name-${idx}`}>
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        value={c?.name ?? ''}
                        onChange={ev => {
                          updateArrayItem('certificates', idx, {
                            name: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>

                    <Form.Group as={Col} controlId={`cert-issuer-${idx}`}>
                      <Form.Label>Issuer</Form.Label>
                      <Form.Control
                        value={c?.issuer ?? ''}
                        onChange={ev => {
                          updateArrayItem('certificates', idx, {
                            issuer: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>

                    <Form.Group as={Col} controlId={`cert-url-${idx}`}>
                      <Form.Label>URL</Form.Label>
                      <Form.Control
                        value={c?.url ?? ''}
                        onChange={ev => {
                          updateArrayItem('certificates', idx, {
                            url: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>

                    <Form.Group as={Col} controlId={`cert-date-${idx}`}>
                      <Form.Label>Date</Form.Label>
                      <Form.Control
                        value={c?.date ?? ''}
                        onChange={ev => {
                          updateArrayItem('certificates', idx, {
                            date: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>
                  </Row>

                  {!readOnly && (
                    <div className='mt-2'>
                      <Button
                        variant='outline-danger'
                        onClick={() => {
                          removeArrayItem('certificates', idx);
                        }}
                      >
                        Remove Certificate
                      </Button>
                    </div>
                  )}
                </Card>
              ))}

              {!readOnly && (
                <Button
                  onClick={() => {
                    addArrayItem('certificates');
                  }}
                >
                  Add Certificate
                </Button>
              )}
            </Card.Body>
          </div>
        </Collapse>
      </Card>

      {/* Publications */}
      <Card className='mb-3'>
        <div className='d-flex align-items-center p-2'>
          <Button
            variant='link'
            onClick={() => {
              setPublicationsOpen(!publicationsOpen);
            }}
            aria-controls='publications-collapse'
            aria-expanded={publicationsOpen}
            className='p-0'
          >
            <span style={{fontSize: '1rem'}}>
              {publicationsOpen ? '▼' : '▶'}
            </span>
          </Button>
          <Card.Title className='ms-2 mb-0'>Publications</Card.Title>
        </div>
        <Collapse in={publicationsOpen}>
          <div id='publications-collapse'>
            <Card.Body>
              {safeArray(form.publications).map((p, idx) => (
                <Card key={`pub-${idx}`} className='mb-2 p-2'>
                  {!readOnly && (
                    <div className='d-flex justify-content-end mb-2'>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        aria-label='Move up'
                        disabled={idx === 0}
                        onClick={() => {
                          moveArrayItem('publications', idx, 'up');
                        }}
                        className='me-1'
                      >
                        ↑
                      </Button>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        aria-label='Move down'
                        disabled={
                          idx === safeArray(form.publications).length - 1
                        }
                        onClick={() => {
                          moveArrayItem('publications', idx, 'down');
                        }}
                      >
                        ↓
                      </Button>
                    </div>
                  )}
                  <Row>
                    <Form.Group as={Col} controlId={`pub-name-${idx}`}>
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        value={p?.name ?? ''}
                        onChange={ev => {
                          updateArrayItem('publications', idx, {
                            name: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>

                    <Form.Group as={Col} controlId={`pub-publisher-${idx}`}>
                      <Form.Label>Publisher</Form.Label>
                      <Form.Control
                        value={p?.publisher ?? ''}
                        onChange={ev => {
                          updateArrayItem('publications', idx, {
                            publisher: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>
                  </Row>

                  <Row className='mb-2'>
                    <Form.Group as={Col} controlId={`pub-url-${idx}`}>
                      <Form.Label>URL</Form.Label>
                      <Form.Control
                        value={p?.url ?? ''}
                        onChange={ev => {
                          updateArrayItem('publications', idx, {
                            url: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>

                    <Form.Group as={Col} controlId={`pub-releaseDate-${idx}`}>
                      <Form.Label>Release Date</Form.Label>
                      <Form.Control
                        value={p?.releaseDate ?? ''}
                        onChange={ev => {
                          updateArrayItem('publications', idx, {
                            releaseDate: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>
                  </Row>

                  <Form.Group className='mb-2' controlId={`pub-summary-${idx}`}>
                    <Form.Label>Summary</Form.Label>
                    <div className='d-flex align-items-start'>
                      <Form.Control
                        as='textarea'
                        rows={2}
                        value={p?.summary ?? ''}
                        onChange={ev => {
                          updateArrayItem('publications', idx, {
                            summary: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                      {aiShowInline && aiRewrite && (
                        <AiRewriteButton
                          disabled={readOnly ?? !(p?.summary ?? '')}
                          busy={
                            aiBusyPath ===
                            pathKey(['resume', 'publications', idx, 'summary'])
                          }
                          onClick={mode => {
                            void aiRewrite(
                              ['resume', 'publications', idx, 'summary'],
                              String(p?.summary ?? ''),
                              mode,
                            );
                          }}
                        />
                      )}
                    </div>
                    {aiError ? (
                      <div className='text-danger small mt-1'>{aiError}</div>
                    ) : null}
                  </Form.Group>

                  {!readOnly && (
                    <div className='mt-2'>
                      <Button
                        variant='outline-danger'
                        onClick={() => {
                          removeArrayItem('publications', idx);
                        }}
                      >
                        Remove Publication
                      </Button>
                    </div>
                  )}
                </Card>
              ))}

              {!readOnly && (
                <Button
                  onClick={() => {
                    addArrayItem('publications');
                  }}
                >
                  Add Publication
                </Button>
              )}
            </Card.Body>
          </div>
        </Collapse>
      </Card>

      {/* Skills */}
      <Card className='mb-3'>
        <div className='d-flex align-items-center p-2'>
          <Button
            variant='link'
            onClick={() => {
              setSkillsOpen(!skillsOpen);
            }}
            aria-controls='skills-collapse'
            aria-expanded={skillsOpen}
            className='p-0'
          >
            <span style={{fontSize: '1rem'}}>{skillsOpen ? '▼' : '▶'}</span>
          </Button>
          <Card.Title className='ms-2 mb-0'>Skills</Card.Title>
        </div>
        <Collapse in={skillsOpen}>
          <div id='skills-collapse'>
            <Card.Body>
              {safeArray(form.skills).map((s, idx) => (
                <Card key={`skill-${idx}`} className='mb-2 p-2'>
                  {!readOnly && (
                    <div className='d-flex justify-content-end mb-2'>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        aria-label='Move up'
                        disabled={idx === 0}
                        onClick={() => {
                          moveArrayItem('skills', idx, 'up');
                        }}
                        className='me-1'
                      >
                        ↑
                      </Button>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        aria-label='Move down'
                        disabled={idx === safeArray(form.skills).length - 1}
                        onClick={() => {
                          moveArrayItem('skills', idx, 'down');
                        }}
                      >
                        ↓
                      </Button>
                    </div>
                  )}
                  <Row>
                    <Form.Group as={Col} controlId={`skill-name-${idx}`}>
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        value={s?.name ?? ''}
                        onChange={ev => {
                          updateArrayItem('skills', idx, {
                            name: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>

                    <Form.Group as={Col} controlId={`skill-level-${idx}`}>
                      <Form.Label>Level</Form.Label>
                      <Form.Control
                        value={s?.level ?? ''}
                        onChange={ev => {
                          updateArrayItem('skills', idx, {
                            level: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>
                  </Row>

                  <Form.Group controlId={`skill-keywords-${idx}`}>
                    <Form.Label>Keywords (comma separated)</Form.Label>
                    <Form.Control
                      value={
                        drafts[`skills-${idx}-keywords`] ??
                        s?.keywords?.join(', ') ??
                        ''
                      }
                      onChange={ev => {
                        const value_ = ev.currentTarget.value;
                        setDrafts(previous => ({
                          ...previous,
                          [`skills-${idx}-keywords`]: value_,
                        }));
                      }}
                      onBlur={ev => {
                        const value_ = ev.currentTarget.value;
                        const array = value_
                          .split(',')
                          .map(t => t.trim())
                          .filter(Boolean);
                        updateArrayItem('skills', idx, {keywords: array});
                        setDrafts(previous => ({
                          ...previous,
                          [`skills-${idx}-keywords`]: array.join(', '),
                        }));
                      }}
                      readOnly={readOnly}
                    />
                  </Form.Group>

                  {!readOnly && (
                    <div className='mt-2'>
                      <Button
                        variant='outline-danger'
                        onClick={() => {
                          removeArrayItem('skills', idx);
                        }}
                      >
                        Remove Skill
                      </Button>
                    </div>
                  )}
                </Card>
              ))}

              {!readOnly && (
                <Button
                  onClick={() => {
                    addArrayItem('skills');
                  }}
                >
                  Add Skill
                </Button>
              )}
            </Card.Body>
          </div>
        </Collapse>
      </Card>

      {/* Languages */}
      <Card className='mb-3'>
        <div className='d-flex align-items-center p-2'>
          <Button
            variant='link'
            onClick={() => {
              setLanguagesOpen(!languagesOpen);
            }}
            aria-controls='languages-collapse'
            aria-expanded={languagesOpen}
            className='p-0'
          >
            <span style={{fontSize: '1rem'}}>{languagesOpen ? '▼' : '▶'}</span>
          </Button>
          <Card.Title className='ms-2 mb-0'>Languages</Card.Title>
        </div>
        <Collapse in={languagesOpen}>
          <div id='languages-collapse'>
            <Card.Body>
              {safeArray(form.languages).map((l, idx) => (
                <Card key={`lang-${idx}`} className='mb-2 p-2'>
                  {!readOnly && (
                    <div className='d-flex justify-content-end mb-2'>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        aria-label='Move up'
                        disabled={idx === 0}
                        onClick={() => {
                          moveArrayItem('languages', idx, 'up');
                        }}
                        className='me-1'
                      >
                        ↑
                      </Button>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        aria-label='Move down'
                        disabled={idx === safeArray(form.languages).length - 1}
                        onClick={() => {
                          moveArrayItem('languages', idx, 'down');
                        }}
                      >
                        ↓
                      </Button>
                    </div>
                  )}
                  <Row>
                    <Form.Group as={Col} controlId={`lang-name-${idx}`}>
                      <Form.Label>Language</Form.Label>
                      <Form.Control
                        value={l?.language ?? ''}
                        onChange={ev => {
                          updateArrayItem('languages', idx, {
                            language: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>

                    <Form.Group as={Col} controlId={`lang-fluency-${idx}`}>
                      <Form.Label>Fluency</Form.Label>
                      <Form.Control
                        value={l?.fluency ?? ''}
                        onChange={ev => {
                          updateArrayItem('languages', idx, {
                            fluency: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>
                  </Row>

                  {!readOnly && (
                    <div className='mt-2'>
                      <Button
                        variant='outline-danger'
                        onClick={() => {
                          removeArrayItem('languages', idx);
                        }}
                      >
                        Remove Language
                      </Button>
                    </div>
                  )}
                </Card>
              ))}

              {!readOnly && (
                <Button
                  onClick={() => {
                    addArrayItem('languages');
                  }}
                >
                  Add Language
                </Button>
              )}
            </Card.Body>
          </div>
        </Collapse>
      </Card>

      {/* Interests */}
      <Card className='mb-3'>
        <div className='d-flex align-items-center p-2'>
          <Button
            variant='link'
            onClick={() => {
              setInterestsOpen(!interestsOpen);
            }}
            aria-controls='interests-collapse'
            aria-expanded={interestsOpen}
            className='p-0'
          >
            <span style={{fontSize: '1rem'}}>{interestsOpen ? '▼' : '▶'}</span>
          </Button>
          <Card.Title className='ms-2 mb-0'>Interests</Card.Title>
        </div>
        <Collapse in={interestsOpen}>
          <div id='interests-collapse'>
            <Card.Body>
              {safeArray(form.interests).map((it, idx) => (
                <Card key={`interest-${idx}`} className='mb-2 p-2'>
                  {!readOnly && (
                    <div className='d-flex justify-content-end mb-2'>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        aria-label='Move up'
                        disabled={idx === 0}
                        onClick={() => {
                          moveArrayItem('interests', idx, 'up');
                        }}
                        className='me-1'
                      >
                        ↑
                      </Button>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        aria-label='Move down'
                        disabled={idx === safeArray(form.interests).length - 1}
                        onClick={() => {
                          moveArrayItem('interests', idx, 'down');
                        }}
                      >
                        ↓
                      </Button>
                    </div>
                  )}
                  <Form.Group controlId={`interest-name-${idx}`}>
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      value={it?.name ?? ''}
                      onChange={ev => {
                        updateArrayItem('interests', idx, {
                          name: ev.currentTarget.value,
                        });
                      }}
                      readOnly={readOnly}
                    />
                  </Form.Group>

                  <Form.Group controlId={`interest-keywords-${idx}`}>
                    <Form.Label>Keywords (comma separated)</Form.Label>
                    <Form.Control
                      value={
                        drafts[`interests-${idx}-keywords`] ??
                        it?.keywords?.join(', ') ??
                        ''
                      }
                      onChange={ev => {
                        const value_ = ev.currentTarget.value;
                        setDrafts(previous => ({
                          ...previous,
                          [`interests-${idx}-keywords`]: value_,
                        }));
                      }}
                      onBlur={ev => {
                        const value_ = ev.currentTarget.value;
                        const array = value_
                          .split(',')
                          .map(t => t.trim())
                          .filter(Boolean);
                        updateArrayItem('interests', idx, {keywords: array});
                        setDrafts(previous => ({
                          ...previous,
                          [`interests-${idx}-keywords`]: array.join(', '),
                        }));
                      }}
                      readOnly={readOnly}
                    />
                  </Form.Group>

                  {!readOnly && (
                    <div className='mt-2'>
                      <Button
                        variant='outline-danger'
                        onClick={() => {
                          removeArrayItem('interests', idx);
                        }}
                      >
                        Remove Interest
                      </Button>
                    </div>
                  )}
                </Card>
              ))}

              {!readOnly && (
                <Button
                  onClick={() => {
                    addArrayItem('interests');
                  }}
                >
                  Add Interest
                </Button>
              )}
            </Card.Body>
          </div>
        </Collapse>
      </Card>

      {/* References */}
      <Card className='mb-3'>
        <div className='d-flex align-items-center p-2'>
          <Button
            variant='link'
            onClick={() => {
              setReferencesOpen(!referencesOpen);
            }}
            aria-controls='references-collapse'
            aria-expanded={referencesOpen}
            className='p-0'
          >
            <span style={{fontSize: '1rem'}}>{referencesOpen ? '▼' : '▶'}</span>
          </Button>
          <Card.Title className='ms-2 mb-0'>References</Card.Title>
        </div>
        <Collapse in={referencesOpen}>
          <div id='references-collapse'>
            <Card.Body>
              {safeArray(form.references).map((r, idx) => (
                <Card key={`ref-${idx}`} className='mb-2 p-2'>
                  {!readOnly && (
                    <div className='d-flex justify-content-end mb-2'>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        aria-label='Move up'
                        disabled={idx === 0}
                        onClick={() => {
                          moveArrayItem('references', idx, 'up');
                        }}
                        className='me-1'
                      >
                        ↑
                      </Button>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        aria-label='Move down'
                        disabled={idx === safeArray(form.references).length - 1}
                        onClick={() => {
                          moveArrayItem('references', idx, 'down');
                        }}
                      >
                        ↓
                      </Button>
                    </div>
                  )}
                  <Form.Group controlId={`ref-name-${idx}`}>
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      value={r?.name ?? ''}
                      onChange={ev => {
                        updateArrayItem('references', idx, {
                          name: ev.currentTarget.value,
                        });
                      }}
                      readOnly={readOnly}
                    />
                  </Form.Group>

                  <Form.Group controlId={`ref-reference-${idx}`}>
                    <Form.Label>Reference</Form.Label>
                    <div className='d-flex align-items-start'>
                      <Form.Control
                        as='textarea'
                        rows={2}
                        value={r?.reference ?? ''}
                        onChange={ev => {
                          updateArrayItem('references', idx, {
                            reference: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                      {aiShowInline && aiRewrite && (
                        <AiRewriteButton
                          disabled={readOnly ?? !(r?.reference ?? '')}
                          busy={
                            aiBusyPath ===
                            pathKey(['resume', 'references', idx, 'reference'])
                          }
                          onClick={mode => {
                            void aiRewrite(
                              ['resume', 'references', idx, 'reference'],
                              String(r?.reference ?? ''),
                              mode,
                            );
                          }}
                        />
                      )}
                    </div>
                    {aiError ? (
                      <div className='text-danger small mt-1'>{aiError}</div>
                    ) : null}
                  </Form.Group>

                  {!readOnly && (
                    <div className='mt-2'>
                      <Button
                        variant='outline-danger'
                        onClick={() => {
                          removeArrayItem('references', idx);
                        }}
                      >
                        Remove Reference
                      </Button>
                    </div>
                  )}
                </Card>
              ))}

              {!readOnly && (
                <Button
                  onClick={() => {
                    addArrayItem('references');
                  }}
                >
                  Add Reference
                </Button>
              )}
            </Card.Body>
          </div>
        </Collapse>
      </Card>

      {/* Projects */}
      <Card className='mb-3'>
        <div className='d-flex align-items-center p-2'>
          <Button
            variant='link'
            onClick={() => {
              setProjectsOpen(!projectsOpen);
            }}
            aria-controls='projects-collapse'
            aria-expanded={projectsOpen}
            className='p-0'
          >
            <span style={{fontSize: '1rem'}}>{projectsOpen ? '▼' : '▶'}</span>
          </Button>
          <Card.Title className='ms-2 mb-0'>Projects</Card.Title>
        </div>
        <Collapse in={projectsOpen}>
          <div id='projects-collapse'>
            <Card.Body>
              {safeArray(form.projects).map((p, idx) => (
                <Card key={`proj-${idx}`} className='mb-2 p-2'>
                  {!readOnly && (
                    <div className='d-flex justify-content-end mb-2'>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        aria-label='Move up'
                        disabled={idx === 0}
                        onClick={() => {
                          moveArrayItem('projects', idx, 'up');
                        }}
                        className='me-1'
                      >
                        ↑
                      </Button>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        aria-label='Move down'
                        disabled={idx === safeArray(form.projects).length - 1}
                        onClick={() => {
                          moveArrayItem('projects', idx, 'down');
                        }}
                      >
                        ↓
                      </Button>
                    </div>
                  )}
                  <Row>
                    <Form.Group as={Col} controlId={`proj-name-${idx}`}>
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        value={p?.name ?? ''}
                        onChange={ev => {
                          updateArrayItem('projects', idx, {
                            name: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>

                    <Form.Group as={Col} controlId={`proj-type-${idx}`}>
                      <Form.Label>Type</Form.Label>
                      <Form.Control
                        value={p?.type ?? ''}
                        onChange={ev => {
                          updateArrayItem('projects', idx, {
                            type: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>
                  </Row>

                  <Form.Group controlId={`proj-description-${idx}`}>
                    <Form.Label>Description</Form.Label>
                    <div className='d-flex align-items-start'>
                      <Form.Control
                        as='textarea'
                        rows={3}
                        value={p?.description ?? ''}
                        onChange={ev => {
                          updateArrayItem('projects', idx, {
                            description: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                      {aiShowInline && aiRewrite && (
                        <AiRewriteButton
                          disabled={readOnly ?? !(p?.description ?? '')}
                          busy={
                            aiBusyPath ===
                            pathKey(['resume', 'projects', idx, 'description'])
                          }
                          onClick={mode => {
                            void aiRewrite(
                              ['resume', 'projects', idx, 'description'],
                              String(p?.description ?? ''),
                              mode,
                            );
                          }}
                        />
                      )}
                    </div>
                    {aiError ? (
                      <div className='text-danger small mt-1'>{aiError}</div>
                    ) : null}
                  </Form.Group>

                  <Row className='mb-2'>
                    <Form.Group as={Col} controlId={`proj-url-${idx}`}>
                      <Form.Label>URL</Form.Label>
                      <Form.Control
                        value={p?.url ?? ''}
                        onChange={ev => {
                          updateArrayItem('projects', idx, {
                            url: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>

                    <Form.Group as={Col} controlId={`proj-entity-${idx}`}>
                      <Form.Label>Entity</Form.Label>
                      <Form.Control
                        value={p?.entity ?? ''}
                        onChange={ev => {
                          updateArrayItem('projects', idx, {
                            entity: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>
                  </Row>

                  <Row className='mb-2'>
                    <Form.Group as={Col} controlId={`proj-start-${idx}`}>
                      <Form.Label>Start Date</Form.Label>
                      <Form.Control
                        value={p?.startDate ?? ''}
                        onChange={ev => {
                          updateArrayItem('projects', idx, {
                            startDate: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>

                    <Form.Group as={Col} controlId={`proj-end-${idx}`}>
                      <Form.Label>End Date</Form.Label>
                      <Form.Control
                        value={p?.endDate ?? ''}
                        onChange={ev => {
                          updateArrayItem('projects', idx, {
                            endDate: ev.currentTarget.value,
                          });
                        }}
                        readOnly={readOnly}
                      />
                    </Form.Group>
                  </Row>

                  <Form.Group controlId={`proj-highlights-${idx}`}>
                    <Form.Label>Highlights</Form.Label>
                    {safeArray(p?.highlights).length === 0 ? (
                      !readOnly && (
                        <div className='mt-2'>
                          <Button
                            onClick={() => {
                              const next = [...safeArray(p?.highlights), ''];
                              updateArrayItem('projects', idx, {
                                highlights: next,
                              });
                            }}
                          >
                            Add Highlight
                          </Button>
                        </div>
                      )
                    ) : (
                      <>
                        {safeArray(p?.highlights).map((h, hIdx) => (
                          <InputGroup
                            className='mb-2'
                            key={`proj-${idx}-highlight-${hIdx}`}
                          >
                            <Form.Control
                              value={typeof h === 'string' ? h : ''}
                              onChange={event => {
                                const next = safeArray(p?.highlights).map(
                                  (it, i) =>
                                    i === hIdx ? event.currentTarget.value : it,
                                );
                                updateArrayItem('projects', idx, {
                                  highlights: next,
                                });
                              }}
                              readOnly={readOnly}
                              placeholder='Describe highlight'
                            />
                            {!readOnly && (
                              <>
                                <Button
                                  variant='outline-secondary'
                                  aria-label='Move up'
                                  disabled={hIdx === 0}
                                  onClick={() => {
                                    moveProjectHighlight(idx, hIdx, 'up');
                                  }}
                                  className='ms-2'
                                >
                                  ↑
                                </Button>
                                <Button
                                  variant='outline-secondary'
                                  aria-label='Move down'
                                  disabled={
                                    hIdx === safeArray(p?.highlights).length - 1
                                  }
                                  onClick={() => {
                                    moveProjectHighlight(idx, hIdx, 'down');
                                  }}
                                  className='ms-1'
                                >
                                  ↓
                                </Button>
                                <Button
                                  variant='outline-danger'
                                  onClick={() => {
                                    const next = safeArray(
                                      p?.highlights,
                                    ).filter((_, i) => i !== hIdx);
                                    updateArrayItem('projects', idx, {
                                      highlights: next,
                                    });
                                  }}
                                  className='ms-1'
                                >
                                  Remove
                                </Button>
                                {aiShowInline && aiRewrite && (
                                  <AiRewriteButton
                                    disabled={readOnly ?? !h}
                                    busy={
                                      aiBusyPath ===
                                      pathKey([
                                        'resume',
                                        'projects',
                                        idx,
                                        'highlights',
                                        hIdx,
                                      ])
                                    }
                                    onClick={mode => {
                                      void aiRewrite(
                                        [
                                          'resume',
                                          'projects',
                                          idx,
                                          'highlights',
                                          hIdx,
                                        ],
                                        String(h ?? ''),
                                        mode,
                                      );
                                    }}
                                  />
                                )}
                              </>
                            )}
                          </InputGroup>
                        ))}
                        {!readOnly && (
                          <div>
                            <Button
                              onClick={() => {
                                const next = [...safeArray(p?.highlights), ''];
                                updateArrayItem('projects', idx, {
                                  highlights: next,
                                });
                              }}
                            >
                              Add Highlight
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </Form.Group>

                  <Form.Group controlId={`proj-keywords-${idx}`}>
                    <Form.Label>Keywords (comma separated)</Form.Label>
                    <Form.Control
                      value={
                        drafts[`projects-${idx}-keywords`] ??
                        p?.keywords?.join(', ') ??
                        ''
                      }
                      onChange={ev => {
                        const value_ = ev.currentTarget.value;
                        setDrafts(previous => ({
                          ...previous,
                          [`projects-${idx}-keywords`]: value_,
                        }));
                      }}
                      onBlur={ev => {
                        const value_ = ev.currentTarget.value;
                        const array = value_
                          .split(',')
                          .map(t => t.trim())
                          .filter(Boolean);
                        updateArrayItem('projects', idx, {keywords: array});
                        setDrafts(previous => ({
                          ...previous,
                          [`projects-${idx}-keywords`]: array.join(', '),
                        }));
                      }}
                      readOnly={readOnly}
                    />
                  </Form.Group>

                  <Form.Group controlId={`proj-roles-${idx}`}>
                    <Form.Label>Roles (comma separated)</Form.Label>
                    <Form.Control
                      value={
                        drafts[`projects-${idx}-roles`] ??
                        p?.roles?.join(', ') ??
                        ''
                      }
                      onChange={ev => {
                        const value_ = ev.currentTarget.value;
                        setDrafts(previous => ({
                          ...previous,
                          [`projects-${idx}-roles`]: value_,
                        }));
                      }}
                      onBlur={ev => {
                        const value_ = ev.currentTarget.value;
                        const array = value_
                          .split(',')
                          .map(t => t.trim())
                          .filter(Boolean);
                        updateArrayItem('projects', idx, {roles: array});
                        setDrafts(previous => ({
                          ...previous,
                          [`projects-${idx}-roles`]: array.join(', '),
                        }));
                      }}
                      readOnly={readOnly}
                    />
                  </Form.Group>

                  {!readOnly && (
                    <div className='mt-2'>
                      <Button
                        variant='outline-danger'
                        onClick={() => {
                          removeArrayItem('projects', idx);
                        }}
                      >
                        Remove Project
                      </Button>
                    </div>
                  )}
                </Card>
              ))}

              {!readOnly && (
                <Button
                  onClick={() => {
                    addArrayItem('projects');
                  }}
                >
                  Add Project
                </Button>
              )}
            </Card.Body>
          </div>
        </Collapse>
      </Card>

      <div className='d-flex justify-content-end'>{actionButtons}</div>
    </Form>
  );
}
